// CRM API Endpoints Configuration
// Based on the API_DOCS.md structure

export const API_ENDPOINTS = {
  // Authentication
  AUTH: {
    LOGIN: "crm/authenticate",
    ME: "crm/me",
  },

  // Products
  PRODUCTS: {
    LIST: "crm/products",
    FEATURED: "crm/products/featured",
    RECENT: "crm/products/recent",
    POPULAR: "crm/products/popular",
    BY_SLUG: (slug: string) => `crm/products/slug/${slug}`,
    BY_ID: (id: string) => `crm/products/${id}`,
    STATUS: (id: string) => `crm/products/${id}/status`,
    TOGGLE_FEATURED: (id: string) => `crm/products/${id}/toggle-featured`,
    TOGGLE_ACTIVE: (id: string) => `crm/products/${id}/toggle-active`,
  },

  // Brands
  BRANDS: {
    LIST: "crm/brands",
    BY_BRANCH: (branchId: string) => `crm/brands/branch/${branchId}`,
    ACTIVE: "crm/brands/active",
    POPULAR: "crm/brands/popular",
    BY_SLUG: (slug: string) => `crm/brands/slug/${slug}`,
    BY_ID: (id: string) => `crm/brands/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/brands/${id}/toggle-active`,
    STATISTICS: (id: string) => `crm/brands/${id}/statistics`,
  },

  // Models
  MODELS: {
    LIST: "crm/models",
    ACTIVE: "crm/models/active",
    POPULAR: "crm/models/popular",
    BY_BRAND: (brandId: string) => `crm/models/brand/${brandId}`,
    BY_SLUG: (slug: string) => `crm/models/slug/${slug}`,
    BY_ID: (id: string) => `crm/models/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/models/${id}/toggle-active`,
    STATISTICS: (id: string) => `crm/models/${id}/statistics`,
  },

  // FAQs
  FAQS: {
    LIST: "crm/faqs",
    ACTIVE: "crm/faqs/active",
    CATEGORIES: "crm/faqs/categories",
    BY_CATEGORY: (category: string) => `crm/faqs/category/${category}`,
    BY_ID: (id: string) => `crm/faqs/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/faqs/${id}/toggle-active`,
    STATISTICS: "crm/faqs/statistics/overview",
  },

  // Reviews
  REVIEWS: {
    LIST: "crm/reviews",
    ACTIVE: "crm/reviews/active",
    BY_STATUS: (status: string) => `crm/reviews/status/${status}`,
    BY_RATING: (rating: number) => `crm/reviews/rating/${rating}`,
    BY_ID: (id: string) => `crm/reviews/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/reviews/${id}/toggle-active`,
    APPROVE: (id: string) => `crm/reviews/${id}/approve`,
    REJECT: (id: string) => `crm/reviews/${id}/reject`,
    STATISTICS: "crm/reviews/statistics/overview",
  },

  // Video Reviews
  VIDEO_REVIEWS: {
    LIST: "crm/video-reviews",
    ACTIVE: "crm/video-reviews/active",
    BY_STATUS: (status: string) => `crm/video-reviews/status/${status}`,
    BY_ROLE: (role: string) => `crm/video-reviews/role/${role}`,
    BY_ID: (id: string) => `crm/video-reviews/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/video-reviews/${id}/toggle-active`,
    APPROVE: (id: string) => `crm/video-reviews/${id}/approve`,
    REJECT: (id: string) => `crm/video-reviews/${id}/reject`,
    STATISTICS: "crm/video-reviews/statistics/overview",
  },

  // Branches
  BRANCHES: {
    LIST: "crm/branches",
    ACTIVE: "crm/branches/active",
    MAIN: "crm/branches/main",
    BY_LOCATION: "crm/branches/location",
    BY_SLUG: (slug: string) => `crm/branches/slug/${slug}`,
    BY_ID: (id: string) => `crm/branches/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/branches/${id}/toggle-active`,
    SET_MAIN: (id: string) => `crm/branches/${id}/set-main`,
    STATISTICS: (id: string) => `crm/branches/${id}/statistics`,
  },

  // Users
  USERS: {
    LIST: "crm/users",
    ACTIVE: "crm/users/active",
    BY_ROLE: (role: string) => `crm/users/role/${role}`,
    BY_BRANCH: (branchId: string) => `crm/users/branch/${branchId}`,
    BY_ID: (id: string) => `crm/users/${id}`,
    TOGGLE_ACTIVE: (id: string) => `crm/users/${id}/toggle-active`,
    CHANGE_PASSWORD: (id: string) => `crm/users/${id}/change-password`,
    STATISTICS: (id: string) => `crm/users/${id}/statistics`,
  },

  // File Upload
  UPLOAD: {
    SINGLE: "crm/upload/single",
    MULTIPLE: "crm/upload/multiple",
    PRESIGNED_URL: "crm/upload/presigned-url",
    DELETE_BY_KEY: (key: string) => `crm/upload/${key}`,
    DELETE_BY_URL: "crm/upload/by-url",
    DELETE_MULTIPLE: "crm/upload/multiple",
    DELETE_MULTIPLE_BY_URLS: "crm/upload/multiple/by-urls",
    VALIDATE_URL: "crm/upload/validate-url",
  },
} as const;

// Data path mappings for different response structures
export const DATA_PATHS = {
  PRODUCTS: "products",
  BRANDS: "brands",
  MODELS: "models",
  FAQS: "faqs",
  REVIEWS: "reviews",
  VIDEO_REVIEWS: "videoReviews",
  BRANCHES: "branches",
  USERS: "users",
  CATEGORIES: "data", // For simple array responses
} as const;

// Query parameter builders
export const buildQueryParams = (params: Record<string, any>): string => {
  const searchParams = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== "") {
      searchParams.append(key, String(value));
    }
  });

  const queryString = searchParams.toString();
  return queryString ? `?${queryString}` : "";
};

// Common query parameters for pagination and filtering
export const COMMON_QUERY_PARAMS = {
  PAGE: "page",
  LIMIT: "limit",
  SEARCH: "search",
  SORT_BY: "sortBy",
  SORT_ORDER: "sortOrder",
  IS_ACTIVE: "isActive",
} as const;

// Pagination defaults
export const PAGINATION_DEFAULTS = {
  PAGE: 1,
  LIMIT: 10,
  MAX_LIMIT: 100,
} as const;
